import React, { useEffect, useState } from 'react';
import { Plus, Shield, Edit2, Trash2, X, Users, Lock, ChevronDown, ChevronRight, Copy, Loader2, RefreshCw } from 'lucide-react';
import { useWorkspace, Permission, permissionCategories, allPermissions } from '../contexts/WorkspaceContext';
import { rolesApi, usersApi, RoleRecord, RolePermissionRow, UserRecord } from '../services/creatorApi';
import { toast } from 'sonner';
import { ConfirmModal } from '../components/ConfirmModal';
export function RolesPermissions() {
  const {
    currentUser,
    refreshRoles
  } = useWorkspace();
  const [roles, setRoles] = useState<RoleRecord[]>([]);
  const [allUsers, setAllUsers] = useState<UserRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRole, setSelectedRole] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [editingRole, setEditingRole] = useState<RoleRecord | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState<RoleRecord | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(Object.keys(permissionCategories)));
  const [newRole, setNewRole] = useState({
    name: '',
    description: '',
    permissions: [] as string[]
  });
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    permissions: [] as string[]
  });
  // Security check
  if (currentUser.Role !== 'Super Admin') {
    return <div className="p-8 text-center" data-id="element-5722">
        <Shield className="mx-auto h-12 w-12 text-gray-400" data-id="element-5723" />
        <h2 className="mt-4 text-lg font-medium text-gray-900" data-id="element-5724">
          Access Denied
        </h2>
        <p className="mt-2 text-sm text-gray-500" data-id="element-5725">
          Only Super Admins can manage roles and permissions.
        </p>
      </div>;
  }
  const loadData = async () => {
    setLoading(true);
    try {
      const [rolesResult, usersResult] = await Promise.all([rolesApi.list(), usersApi.list()]);
      setRoles(rolesResult);
      setAllUsers(usersResult);
    } catch (err) {
      console.error('Failed to load roles:', err);
      toast.error('Failed to load roles');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    loadData();
  }, []);
  const toggleCategory = (category: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) newExpanded.delete(category);else newExpanded.add(category);
    setExpandedCategories(newExpanded);
  };
  const togglePermission = (permission: string, isEdit: boolean) => {
    const setter = isEdit ? setEditForm : setNewRole;
    setter((prev: any) => ({
      ...prev,
      permissions: prev.permissions.includes(permission) ? prev.permissions.filter((p: string) => p !== permission) : [...prev.permissions, permission]
    }));
  };
  const toggleAllInCategory = (perms: string[], isEdit: boolean) => {
    const setter = isEdit ? setEditForm : setNewRole;
    setter((prev: any) => {
      const allSelected = perms.every(p => prev.permissions.includes(p));
      return {
        ...prev,
        permissions: allSelected ? prev.permissions.filter((p: string) => !perms.includes(p)) : [...new Set([...prev.permissions, ...perms])]
      };
    });
  };
  const handleCreateRole = async () => {
    if (!newRole.name.trim()) return;
    setIsSaving(true);
    try {
      const permRows: RolePermissionRow[] = newRole.permissions.map(p => ({
        Permission_Key: p
      }));
      await rolesApi.create({
        Role_Name: newRole.name,
        Description: newRole.description,
        Is_System: false,
        Role_Permissions: permRows
      });
      toast.success('Role created');
      setNewRole({
        name: '',
        description: '',
        permissions: []
      });
      setIsCreating(false);
      loadData();
      refreshRoles();
    } catch (err) {
      console.error('Failed to create role:', err);
      toast.error('Failed to create role');
    } finally {
      setIsSaving(false);
    }
  };
  const handleStartEdit = (roleId: string) => {
    const role = roles.find(r => r.ID === roleId);
    if (role) {
      setEditForm({
        name: role.Role_Name,
        description: role.Description || '',
        permissions: (role.Role_Permissions || []).filter(Boolean).map(p => p.Permission_Key)
      });
      setEditingRole(role);
    }
  };
  const handleSaveEdit = async () => {
    if (!editingRole || !editForm.name.trim()) return;
    setIsSaving(true);
    try {
      // Preserve existing subform row IDs for permissions that already exist
      const existingPerms = (editingRole.Role_Permissions || []).filter(Boolean);
      const permRows: RolePermissionRow[] = editForm.permissions.map(p => {
        const existingRow = existingPerms.find(r => r.Permission_Key === p);
        return {
          ...(existingRow?.ID ? {
            ID: existingRow.ID
          } : {}),
          Permission_Key: p
        };
      });
      await rolesApi.update(editingRole.ID, {
        Role_Name: editForm.name,
        Description: editForm.description,
        Role_Permissions: permRows
      });
      toast.success('Role updated');
      setEditingRole(null);
      loadData();
      refreshRoles();
    } catch (err) {
      console.error('Failed to update role:', err);
      toast.error('Failed to update role');
    } finally {
      setIsSaving(false);
    }
  };
  const handleDeleteRole = (roleId: string) => {
    const role = roles.find(r => r.ID === roleId);
    if (role?.Is_System) {
      toast.error('Cannot delete system roles');
      return;
    }
    setRoleToDelete(role || null);
  };
  const confirmDelete = async () => {
    if (!roleToDelete) return;
    try {
      await rolesApi.delete(roleToDelete.ID);
      toast.success('Role deleted');
      loadData();
      refreshRoles();
    } catch (err) {
      console.error('Failed to delete role:', err);
      toast.error('Failed to delete role');
    }
    setRoleToDelete(null);
  };
  const handleDuplicateRole = async (roleId: string) => {
    const role = roles.find(r => r.ID === roleId);
    if (!role) return;
    setIsSaving(true);
    try {
      // Strip IDs from permission rows — this is a new record, Creator assigns new IDs
      const permRows: RolePermissionRow[] = (role.Role_Permissions || []).filter(Boolean).map(p => ({
        Permission_Key: p.Permission_Key
      }));
      await rolesApi.create({
        Role_Name: `${role.Role_Name} (Copy)`,
        Description: role.Description,
        Is_System: false,
        Role_Permissions: permRows
      });
      toast.success('Role duplicated');
      loadData();
      refreshRoles();
    } catch (err) {
      toast.error('Failed to duplicate role');
    } finally {
      setIsSaving(false);
    }
  };
  const getUsersWithRole = (roleName: string) => {
    return allUsers.filter(u => u.Role === roleName);
  };
  const formatPermissionName = (permission: string) => {
    const parts = permission.split('.');
    const action = parts[parts.length - 1];
    return action.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };
  const renderPermissionEditor = (currentPermissions: string[], isEdit: boolean) => <div className="space-y-2" data-id="element-5726">
      {Object.entries(permissionCategories).map(([category, perms]) => {
      const isExpanded = expandedCategories.has(category);
      const selectedCount = perms.filter(p => currentPermissions.includes(p)).length;
      const allSelected = selectedCount === perms.length;
      return <div key={category} className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-5727">
            <button type="button" onClick={() => toggleCategory(category)} className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors" data-id="element-5728">
              <div className="flex items-center gap-3" data-id="element-5729">
                {isExpanded ? <ChevronDown className="h-4 w-4 text-gray-500" data-id="element-5730" /> : <ChevronRight className="h-4 w-4 text-gray-500" data-id="element-5731" />}
                <span className="font-medium text-gray-900" data-id="element-5732">{category}</span>
                <span className="text-xs text-gray-500" data-id="element-5733">
                  ({selectedCount}/{perms.length})
                </span>
              </div>
              <button type="button" onClick={e => {
            e.stopPropagation();
            toggleAllInCategory(perms, isEdit);
          }} className={`text-xs px-2 py-1 rounded ${allSelected ? 'bg-blue-100 text-blue-700' : 'bg-gray-200 text-gray-600'} hover:opacity-80`} data-id="element-5734">
                {allSelected ? 'Deselect All' : 'Select All'}
              </button>
            </button>
            {isExpanded && <div className="p-4 grid grid-cols-2 gap-2" data-id="element-5735">
                {perms.map(perm => <label key={perm} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded" data-id="element-5736">
                    <input type="checkbox" checked={currentPermissions.includes(perm)} onChange={() => togglePermission(perm, isEdit)} className="h-4 w-4 text-blue-600 rounded border-gray-300" data-id="element-5737" />
                    <span className="text-sm text-gray-700" data-id="element-5738">
                      {formatPermissionName(perm)}
                    </span>
                  </label>)}
              </div>}
          </div>;
    })}
    </div>;
  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]" data-id="element-5739">
        <Loader2 className="h-8 w-8 text-blue-500 animate-spin" data-id="element-5740" />
      </div>;
  }
  return <div className="space-y-6" data-id="element-5741">
      <div className="flex items-center justify-between" data-id="element-5742">
        <div data-id="element-5743">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-5744">
            Roles & Permissions
          </h1>
          <p className="mt-1 text-sm text-gray-500" data-id="element-5745">
            Manage user roles and their access permissions.
          </p>
        </div>
        <div className="flex items-center gap-3" data-id="element-5746">
          <button onClick={loadData} disabled={loading} className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md" title="Refresh" data-id="element-5747">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} data-id="element-5748" />
          </button>
          <button onClick={() => setIsCreating(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700" data-id="element-5749">
            <Plus className="h-4 w-4" data-id="element-5750" />
            Create Role
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4" data-id="element-5751">
        <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-5752">
          <div className="flex items-center gap-3" data-id="element-5753">
            <div className="p-2 bg-purple-50 rounded-lg" data-id="element-5754">
              <Shield className="h-5 w-5 text-purple-600" data-id="element-5755" />
            </div>
            <div data-id="element-5756">
              <p className="text-2xl font-semibold text-gray-900" data-id="element-5757">
                {roles.length}
              </p>
              <p className="text-xs text-gray-500" data-id="element-5758">Total Roles</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-5759">
          <div className="flex items-center gap-3" data-id="element-5760">
            <div className="p-2 bg-blue-50 rounded-lg" data-id="element-5761">
              <Lock className="h-5 w-5 text-blue-600" data-id="element-5762" />
            </div>
            <div data-id="element-5763">
              <p className="text-2xl font-semibold text-gray-900" data-id="element-5764">
                {roles.filter(r => r.Is_System).length}
              </p>
              <p className="text-xs text-gray-500" data-id="element-5765">System Roles</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-5766">
          <div className="flex items-center gap-3" data-id="element-5767">
            <div className="p-2 bg-green-50 rounded-lg" data-id="element-5768">
              <Users className="h-5 w-5 text-green-600" data-id="element-5769" />
            </div>
            <div data-id="element-5770">
              <p className="text-2xl font-semibold text-gray-900" data-id="element-5771">
                {allPermissions.length}
              </p>
              <p className="text-xs text-gray-500" data-id="element-5772">Available Permissions</p>
            </div>
          </div>
        </div>
      </div>

      {/* Create Role Modal */}
      {isCreating && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-5773">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-5774">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setIsCreating(false)} data-id="element-5775" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden" data-id="element-5776">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between" data-id="element-5777">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-5778">
                  Create New Role
                </h3>
                <button onClick={() => setIsCreating(false)} className="text-gray-400 hover:text-gray-500" data-id="element-5779">
                  <X className="h-5 w-5" data-id="element-5780" />
                </button>
              </div>
              <div className="px-6 py-4 space-y-4 overflow-y-auto max-h-[60vh]" data-id="element-5781">
                <div data-id="element-5782">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5783">
                    Role Name
                  </label>
                  <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={newRole.name} onChange={e => setNewRole({
                ...newRole,
                name: e.target.value
              })} placeholder="e.g., Content Manager" data-id="element-5784" />
                </div>
                <div data-id="element-5785">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5786">
                    Description
                  </label>
                  <textarea rows={2} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={newRole.description} onChange={e => setNewRole({
                ...newRole,
                description: e.target.value
              })} placeholder="Brief description..." data-id="element-5787" />
                </div>
                <div data-id="element-5788">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-5789">
                    Permissions
                  </label>
                  {renderPermissionEditor(newRole.permissions, false)}
                </div>
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3" data-id="element-5790">
                <button onClick={() => setIsCreating(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-5791">
                  Cancel
                </button>
                <button onClick={handleCreateRole} disabled={!newRole.name.trim() || isSaving} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50" data-id="element-5792">
                  {isSaving && <Loader2 className="h-4 w-4 animate-spin" data-id="element-5793" />}
                  Create Role
                </button>
              </div>
            </div>
          </div>
        </div>}

      {/* Edit Role Modal */}
      {editingRole && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-5794">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-5795">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setEditingRole(null)} data-id="element-5796" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden" data-id="element-5797">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between" data-id="element-5798">
                <div data-id="element-5799">
                  <h3 className="text-lg font-medium text-gray-900" data-id="element-5800">
                    Edit Role
                  </h3>
                  {roles.find(r => r.ID === editingRole)?.Is_System && <p className="text-xs text-purple-600 flex items-center gap-1 mt-1" data-id="element-5801">
                      <Lock className="h-3 w-3" data-id="element-5802" /> System role — name cannot be
                      changed
                    </p>}
                </div>
                <button onClick={() => setEditingRole(null)} className="text-gray-400 hover:text-gray-500" data-id="element-5803">
                  <X className="h-5 w-5" data-id="element-5804" />
                </button>
              </div>
              <div className="px-6 py-4 space-y-4 overflow-y-auto max-h-[60vh]" data-id="element-5805">
                <div data-id="element-5806">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5807">
                    Role Name
                  </label>
                  <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm disabled:bg-gray-100 disabled:text-gray-500" value={editForm.name} onChange={e => setEditForm({
                ...editForm,
                name: e.target.value
              })} disabled={roles.find(r => r.ID === editingRole)?.Is_System} data-id="element-5808" />
                </div>
                <div data-id="element-5809">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5810">
                    Description
                  </label>
                  <textarea rows={2} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={editForm.description} onChange={e => setEditForm({
                ...editForm,
                description: e.target.value
              })} data-id="element-5811" />
                </div>
                <div data-id="element-5812">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-5813">
                    Permissions
                  </label>
                  {renderPermissionEditor(editForm.permissions, true)}
                </div>
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3" data-id="element-5814">
                <button onClick={() => setEditingRole(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-5815">
                  Cancel
                </button>
                <button onClick={handleSaveEdit} disabled={isSaving} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50" data-id="element-5816">
                  {isSaving && <Loader2 className="h-4 w-4 animate-spin" data-id="element-5817" />}
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>}

      {/* Roles Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-5818">
        {roles.map(role => {
        const roleUsers = getUsersWithRole(role.Role_Name);
        const isSelected = selectedRole === role.ID;
        const permCount = role.Role_Permissions?.length || 0;
        return <div key={role.ID} className={`bg-white border rounded-lg overflow-hidden transition-all ${isSelected ? 'border-blue-500 ring-2 ring-blue-100' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-5819">
              <div className="p-4 cursor-pointer" onClick={() => setSelectedRole(isSelected ? null : role.ID)} data-id="element-5820">
                <div className="flex items-start justify-between mb-3" data-id="element-5821">
                  <div className="flex items-center gap-2" data-id="element-5822">
                    <div className={`p-2 rounded-lg ${role.Is_System ? 'bg-purple-50' : 'bg-blue-50'}`} data-id="element-5823">
                      <Shield className={`h-5 w-5 ${role.Is_System ? 'text-purple-600' : 'text-blue-600'}`} data-id="element-5824" />
                    </div>
                    <div data-id="element-5825">
                      <h3 className="font-medium text-gray-900" data-id="element-5826">
                        {role.Role_Name}
                      </h3>
                      {role.Is_System && <span className="inline-flex items-center gap-1 text-xs text-purple-600" data-id="element-5827">
                          <Lock className="h-3 w-3" data-id="element-5828" /> System
                        </span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1" data-id="element-5829">
                    <button onClick={e => {
                  e.stopPropagation();
                  handleDuplicateRole(role.ID);
                }} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded" title="Duplicate" data-id="element-5830">
                      <Copy className="h-4 w-4" data-id="element-5831" />
                    </button>
                    <button onClick={e => {
                  e.stopPropagation();
                  handleStartEdit(role.ID);
                }} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded" title="Edit" data-id="element-5832">
                      <Edit2 className="h-4 w-4" data-id="element-5833" />
                    </button>
                    {!role.Is_System && <button onClick={e => {
                  e.stopPropagation();
                  handleDeleteRole(role.ID);
                }} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded" title="Delete" data-id="element-5834">
                        <Trash2 className="h-4 w-4" data-id="element-5835" />
                      </button>}
                  </div>
                </div>
                <p className="text-sm text-gray-500 mb-3 line-clamp-2" data-id="element-5836">
                  {role.Description}
                </p>
                <div className="flex items-center justify-between text-sm" data-id="element-5837">
                  <span className="text-gray-500" data-id="element-5838">
                    <span className="font-medium text-gray-900" data-id="element-5839">
                      {permCount}
                    </span>{' '}
                    permissions
                  </span>
                  <span className="text-gray-500" data-id="element-5840">
                    <span className="font-medium text-gray-900" data-id="element-5841">
                      {roleUsers.length}
                    </span>{' '}
                    users
                  </span>
                </div>
              </div>

              {isSelected && <div className="border-t border-gray-100 p-4 bg-gray-50 space-y-3" data-id="element-5842">
                  <div data-id="element-5843">
                    <h4 className="text-xs font-medium text-gray-500 uppercase mb-2" data-id="element-5844">
                      Permissions by Category
                    </h4>
                    <div className="space-y-1" data-id="element-5845">
                      {Object.entries(permissionCategories).map(([category, perms]) => {
                  const rolePerms = (role.Role_Permissions || []).filter(Boolean).map(p => p.Permission_Key);
                  const count = perms.filter(p => rolePerms.includes(p)).length;
                  if (count === 0) return null;
                  return <div key={category} className="flex items-center justify-between text-sm" data-id="element-5846">
                              <span className="text-gray-600" data-id="element-5847">{category}</span>
                              <span className={`font-medium ${count === perms.length ? 'text-green-600' : 'text-blue-600'}`} data-id="element-5848">
                                {count}/{perms.length}
                              </span>
                            </div>;
                })}
                    </div>
                  </div>
                  {roleUsers.length > 0 && <div data-id="element-5849">
                      <h4 className="text-xs font-medium text-gray-500 uppercase mb-2" data-id="element-5850">
                        Users with this role
                      </h4>
                      <div className="flex flex-wrap gap-1" data-id="element-5851">
                        {roleUsers.slice(0, 5).map(user => <span key={user.ID} className="inline-flex items-center px-2 py-1 rounded text-xs bg-white border border-gray-200 text-gray-700" data-id="element-5852">
                            {user.User_Name}
                          </span>)}
                        {roleUsers.length > 5 && <span className="text-xs text-gray-500" data-id="element-5853">
                            +{roleUsers.length - 5} more
                          </span>}
                      </div>
                    </div>}
                </div>}
            </div>;
      })}
      </div>

      {roles.length === 0 && <div className="bg-white border border-gray-200 rounded-lg p-12 text-center" data-id="element-5854">
          <Shield className="mx-auto h-12 w-12 text-gray-400" data-id="element-5855" />
          <h3 className="mt-2 text-sm font-semibold text-gray-900" data-id="element-5856">
            No roles defined
          </h3>
          <p className="mt-1 text-sm text-gray-500" data-id="element-5857">
            Create roles in Creator or click "Create Role" above.
          </p>
        </div>}

      <ConfirmModal isOpen={!!roleToDelete} title="Delete Role" message={`Delete role "${roleToDelete?.Role_Name}"?`} confirmLabel="Delete" variant="danger" onCancel={() => setRoleToDelete(null)} onConfirm={confirmDelete} data-id="element-5858" />
    </div>;
}